import { useEffect, useId, useState } from 'react';
import { Controller, useFormContext, useWatch, type Control } from 'react-hook-form';
import { RotateCcw } from 'lucide-react';
import { DashboardPageFormBody, DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import { ThemeMode } from '@shigo-ui/theme';
import { Button, FilePicker, Input } from '@shigo-ui/ui';

import { NetworkBrandLogoPreview, NetworkBrandThemePreview } from './NetworkBrandThemePreview';
import { dashboardT } from '../../../../../../lib/messages';
import { emptyNetworkFormValue, type NetworkFormValue } from '../../../networkFormSchema';

const DEFAULT_SIDEBAR = '#141B2D';
const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

const previewThemes = [ThemeMode.Light, ThemeMode.Dark] as const;

export function NetworkBrandDetailsFields({ editing = false }: { editing?: boolean }) {
  'use no memo';
  const { control, setValue } = useFormContext<NetworkFormValue>();
  const brandDetails = useWatch({ control, name: 'brandDetails' });
  const logoPreviewUrl = useLogoPreviewUrl(brandDetails.logoFile, brandDetails.logoUrl);
  const lightLogoPreviewUrl = useLogoPreviewUrl(
    brandDetails.logoLightFile,
    brandDetails.logoLightUrl,
  );
  const accentId = useId();
  const accentStrongId = useId();

  return (
    <DashboardPageFormBody
      width="lg"
      className="max-w-7xl"
      title={dashboardT('networks.brand.title')}
      description={dashboardT('networks.brand.description')}
    >
      <div className="grid gap-5 lg:grid-cols-[320px_minmax(0,1fr)] lg:items-start">
        <div className="grid gap-5">
          <section className="border-border bg-card grid gap-4 rounded-lg border p-5">
            <div>
              <h3 className="font-semibold">{dashboardT('networks.brand.logos')}</h3>
              <p className="text-muted-foreground mt-1 text-sm">
                {dashboardT('networks.brand.logosDescription')}
              </p>
            </div>
            <NetworkBrandLogoField
              mode={ThemeMode.Light}
              previewUrl={logoPreviewUrl}
              required={!editing}
            />
            <NetworkBrandLogoField
              mode={ThemeMode.Dark}
              previewUrl={lightLogoPreviewUrl}
              required={!editing}
            />
            <p className="text-muted-foreground text-sm">
              {dashboardT('networks.brand.logoHelper')}
            </p>
          </section>

          <section className="border-border bg-card grid gap-4 rounded-lg border p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-semibold">{dashboardT('networks.brand.palette')}</h3>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  const defaults = emptyNetworkFormValue().brandDetails;
                  setValue('brandDetails.accent', defaults.accent, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setValue('brandDetails.accentStrong', defaults.accentStrong, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setValue('brandDetails.sidebar', defaults.sidebar, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
              >
                <RotateCcw className="size-3.5" aria-hidden="true" />
                {dashboardT('networks.brand.reset')}
              </Button>
              <p className="text-muted-foreground mt-1 text-sm">
                {dashboardT('networks.brand.paletteDescription')}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
              {renderColorField({
                control,
                id: accentId,
                name: 'brandDetails.accent',
                label: dashboardT('networks.brand.accent'),
              })}
              {renderColorField({
                control,
                id: accentStrongId,
                name: 'brandDetails.accentStrong',
                label: dashboardT('networks.brand.accentStrong'),
              })}
            </div>
            <Controller
              control={control}
              name="brandDetails.sidebar"
              render={({ field, fieldState }) =>
                renderColorInput({
                  id: 'network-brand-sidebar',
                  label: dashboardT('networks.brand.sidebar'),
                  value: field.value || DEFAULT_SIDEBAR,
                  error: fieldState.error?.message,
                  onChange: field.onChange,
                  onBlur: field.onBlur,
                })
              }
            />
          </section>
        </div>

        <section className="border-border bg-card overflow-hidden rounded-lg border lg:sticky lg:top-5">
          <div className="border-border border-b px-5 py-4">
            <h3 className="font-semibold">{dashboardT('networks.brand.preview')}</h3>
            <p className="text-muted-foreground mt-1 text-sm">
              {dashboardT('networks.brand.previewDescription')}
            </p>
          </div>
          <div className="grid gap-4 p-4">
            {previewThemes.map((mode) => (
              <NetworkBrandThemePreview
                {...{
                  mode,
                  logoUrl: lightLogoPreviewUrl,
                  accent: brandDetails.accent,
                  accentStrong: brandDetails.accentStrong,
                  sidebarOverride: brandDetails.sidebar,
                }}
                key={mode}
              />
            ))}
          </div>
        </section>
      </div>
    </DashboardPageFormBody>
  );
}

function NetworkBrandLogoField({
  mode,
  previewUrl,
  required = false,
}: {
  mode: ThemeMode.Light | ThemeMode.Dark;
  previewUrl: string;
  required?: boolean;
}) {
  const { control } = useFormContext<NetworkFormValue>();
  const lightLogo = mode === ThemeMode.Dark;
  const label = dashboardT(lightLogo ? 'networks.brand.logoLight' : 'networks.brand.logo');
  const existingUrl = useWatch({
    control,
    name: lightLogo ? 'brandDetails.logoLightUrl' : 'brandDetails.logoUrl',
  });

  return (
    <Controller
      control={control}
      name={lightLogo ? 'brandDetails.logoLightFile' : 'brandDetails.logoFile'}
      render={({ field, fieldState }) => {
        const hasLogo = existingUrl.length > 0 || field.value !== null;
        return (
          <div className="grid content-start gap-3">
            <div
              className="flex h-28 items-center justify-center rounded-md border border-zinc-700 p-4"
              style={{ backgroundColor: lightLogo ? DEFAULT_SIDEBAR : '#FFFFFF' }}
            >
              <NetworkBrandLogoPreview
                {...{
                  url: previewUrl,
                  alt: dashboardT(
                    lightLogo ? 'networks.brand.lightLogoPreview' : 'networks.brand.logoPreview',
                  ),
                  className: 'h-14',
                  inverse: lightLogo,
                }}
              />
            </div>
            <FilePicker
              label={label}
              value={field.value}
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              buttonLabel={dashboardT(
                hasLogo ? 'networks.brand.changeLogo' : 'networks.brand.chooseLogo',
              )}
              buttonClassName="w-full justify-center"
              showSelectedFileName={false}
              clearable={false}
              required={required}
              error={fieldState.error?.message}
              onFileChange={(file) => {
                field.onChange(file);
              }}
            />
          </div>
        );
      }}
    />
  );
}

function useLogoPreviewUrl(file: File | null, existingUrl: string) {
  const [fileUrl, setFileUrl] = useState('');

  useEffect(() => {
    if (!file) return;
    const reader = new FileReader();
    const handleLoad = () => {
      if (typeof reader.result === 'string') setFileUrl(reader.result);
    };
    reader.addEventListener('load', handleLoad);
    reader.readAsDataURL(file);
    return () => {
      reader.removeEventListener('load', handleLoad);
      reader.abort();
    };
  }, [file]);

  return file ? fileUrl : existingUrl;
}

function renderColorField({
  control,
  id,
  name,
  label,
}: {
  control: Control<NetworkFormValue>;
  id: string;
  name: 'brandDetails.accent' | 'brandDetails.accentStrong';
  label: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) =>
        renderColorInput({
          id,
          label,
          value: field.value,
          error: fieldState.error?.message,
          onChange: field.onChange,
          onBlur: field.onBlur,
        })
      }
    />
  );
}

function renderColorInput({
  id,
  label,
  value,
  disabled = false,
  error,
  onChange,
  onBlur,
}: {
  id: string;
  label: string;
  value: string;
  disabled?: boolean;
  error?: string | undefined;
  onChange: (value: string) => void;
  onBlur: () => void;
}) {
  const colorValue = HEX_COLOR_PATTERN.test(value) ? value : '#000000';
  return (
    <DashboardPageFormField label={label} htmlFor={id} error={error}>
      <div className="flex gap-2">
        <Input
          aria-label={`${label} ${dashboardT('networks.brand.colorPicker')}`}
          type="color"
          value={colorValue}
          disabled={disabled}
          className="h-9 w-12 shrink-0 cursor-pointer p-1"
          onChange={(event) => {
            onChange(event.target.value.toUpperCase());
          }}
          onBlur={onBlur}
        />
        <Input
          id={id}
          value={value}
          disabled={disabled}
          placeholder="#1A88E0"
          maxLength={7}
          spellCheck={false}
          aria-invalid={Boolean(error)}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          onBlur={onBlur}
        />
      </div>
    </DashboardPageFormField>
  );
}
